import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import { Result } from '@opengov/components-result';

interface RemoteAppErrorBoundaryProps {
  children: ReactNode;
  /** Height of the error fallback container. Default: '100%' */
  fallbackHeight?: string;
}

/**
 * Shared ErrorBoundary wrapper for remote app shells.
 * Renders a centered error Result with a "Try Again" button that reloads the page.
 */
export function RemoteAppErrorBoundary({
  children,
  fallbackHeight = '100%',
}: RemoteAppErrorBoundaryProps) {
  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: fallbackHeight,
          }}
        >
          <Result
            title="Something went wrong"
            subTitle={(error instanceof Error && error.message) || 'An unexpected error occurred'}
            size="large"
            status="error"
            actionButtons={[{ label: 'Try Again', onClick: resetErrorBoundary }]}
          />
        </Box>
      )}
      onReset={() => window.location.reload()}
    >
      {children}
    </ErrorBoundary>
  );
}
